import type { Diagnostic } from "@codemirror/lint";
import type { EditorView } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";
import { checkSyntax } from "../runner/execute.ts";

// Inline syntax diagnostics for the editor.
//
// Two sources, because neither alone is good enough.
//
// Sucrase is the parser that actually decides whether a run happens, so it is
// the authority on *whether* the code is broken and it gives a real message
// ("Unterminated string constant"). But it is a transpiler: it stops at the
// first error, so it can only ever report one.
//
// Lezer, the incremental parser already driving syntax highlighting, is free
// (it has parsed the document either way) and marks every place it could not
// make sense of. Its messages are generic, but it finds all of them.
//
// So: sucrase for the message you act on, Lezer for the rest of the underlines.

/** Ignore error nodes narrower than this when they sit adjacent to a real one. */
const MERGE_DISTANCE = 2;

function lezerErrors(view: EditorView): Diagnostic[] {
  const found: Diagnostic[] = [];
  const docLength = view.state.doc.length;
  syntaxTree(view.state).iterate({
    enter: (node) => {
      if (!node.type.isError) return;
      // A zero-width error node is the parser flagging a missing token. Widen it
      // by one character so there is something to underline and hover, and clamp
      // to the document so the diagnostic is never out of range.
      const from = Math.min(node.from, docLength);
      const to = Math.min(Math.max(node.to, node.from + 1), docLength);
      if (from >= to) return;
      found.push({
        from,
        to,
        severity: "error",
        message: "Unexpected syntax here.",
      });
    },
  });
  return found;
}

/**
 * Diagnostics for the current document.
 *
 * Exported and taking a view so it can be handed straight to `linter()`, which
 * debounces it; parsing thirty lines is sub-millisecond, but there is no reason
 * to do it on every keystroke.
 */
export function syntaxDiagnostics(view: EditorView): Diagnostic[] {
  const code = view.state.doc.toString();
  const docLength = view.state.doc.length;
  const failure = checkSyntax(code);

  if (!failure) {
    // Sucrase parsed it, so anything Lezer is still unhappy about is a
    // disagreement between the two parsers rather than a problem the user can
    // act on. Trust the parser that gates the run and show nothing.
    return [];
  }

  const from = Math.min(failure.pos, docLength);
  // Underline to the end of the offending token rather than a single character,
  // so the mark is findable. Falls back to one character at the very end of the
  // document, where there is no token left to measure.
  const rest = code.slice(from);
  const tokenLength = /^\s*[A-Za-z0-9_$]+/.exec(rest)?.[0].length ?? 1;
  const to = Math.min(Math.max(from + tokenLength, from + 1), docLength);

  const primary: Diagnostic = {
    from: Math.min(from, Math.max(0, docLength - 1)),
    to,
    severity: "error",
    message: failure.message,
  };

  // Keep Lezer's other marks, minus any that overlap or abut the real one, so
  // the same mistake is not underlined twice with two different messages.
  const secondary = lezerErrors(view).filter(
    (d) => d.to < primary.from - MERGE_DISTANCE || d.from > primary.to + MERGE_DISTANCE,
  );

  return [primary, ...secondary];
}
